/**
 * Sentry wrapper for the public bundle (M+5).
 *
 * # What this module does
 *
 * Initializes `@sentry/react` once at app boot, BUT only when
 * `VITE_SENTRY_DSN` is set. When the DSN is unset / empty / whitespace
 * the module short-circuits to a no-op so local dev and CI stay quiet
 * (no startup failures, no warnings, no network calls).
 *
 * # PII discipline (briefing constraint #4)
 *
 *   - `event.user.email`           → dropped
 *   - `event.request.cookies`      → dropped entirely
 *   - any `email` field in extras/contexts → redacted to `[redacted]`
 *
 * The redaction is shallow on extras/contexts (one level deep) — Sentry
 * already truncates deeply nested objects, and walking the whole tree
 * for every event is expensive. The fields most likely to leak PII in
 * this bundle are the checkout form payload (sent as `extra` from
 * `useConfigurator`), which is shallow.
 *
 * # Session Replay
 *
 * Disabled (`replaysSessionSampleRate: 0`, `replaysOnErrorSampleRate: 0`).
 * The replay product captures keystrokes and screenshots — both are
 * incompatible with GDPR on a checkout form that collects names, email
 * addresses, and order details.
 *
 * # Release tag
 *
 * `release = VITE_GIT_SHA || 'dev'`. CI sets `VITE_GIT_SHA` from
 * `CI_COMMIT_SHORT_SHA` (see `.gitlab-ci.yml` `build:public`). Local dev
 * builds tag every event as `dev` — useful in the Sentry UI to filter
 * out noise from local exploration.
 *
 * # Test seam
 *
 * `setSentryEnvForTest(...)` overrides `import.meta.env` for the
 * duration of a test. Tests mock `@sentry/react` at the module-graph
 * level (`vi.mock`) and assert on the recorded `init(...)` call args.
 */

import * as Sentry from '@sentry/react';

interface SentryEnvOverride {
  dsn?: string;
  gitSha?: string;
}

let __testEnvOverride: SentryEnvOverride | undefined;
let __initialized = false;

/**
 * Test-only — override the env values for one test case. Pass
 * `undefined` to restore the production reads.
 */
export function setSentryEnvForTest(value: SentryEnvOverride | undefined): void {
  __testEnvOverride = value;
  // Allow re-initialisation in tests; production code calls `initSentry`
  // exactly once at boot.
  __initialized = false;
}

function readEnv(): { dsn?: string; gitSha?: string } {
  if (__testEnvOverride) return __testEnvOverride;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const meta = (import.meta as any).env as Record<string, string> | undefined;
    return {
      dsn: meta?.['VITE_SENTRY_DSN'],
      gitSha: meta?.['VITE_GIT_SHA'],
    };
  } catch {
    return {};
  }
}

// Type alias kept loose because @sentry/react's `Event` shape evolves
// across minor versions; the scrubber doesn't need the full graph.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SentryEventLike = any;

/**
 * Strip PII from a Sentry event. Exposed for direct unit testing.
 * Returns `null` to drop the event, an Event-like object to send it.
 */
export function scrubEvent(event: SentryEventLike): SentryEventLike {
  if (!event) return event;
  // 1. user.email — strip even when set explicitly by another integration.
  if (event.user && typeof event.user === 'object') {
    if ('email' in event.user) {
      delete event.user.email;
    }
  }
  // 2. request.cookies — drop entirely. The public bundle sets no
  // tracking cookies today but a future regression must not leak them.
  if (event.request && typeof event.request === 'object') {
    if ('cookies' in event.request) {
      delete event.request.cookies;
    }
  }
  // 3. Shallow `email` redaction in extras + contexts.
  redactEmailField(event.extra);
  if (event.contexts && typeof event.contexts === 'object') {
    for (const key of Object.keys(event.contexts)) {
      redactEmailField(event.contexts[key]);
    }
  }
  return event;
}

function redactEmailField(obj: unknown): void {
  if (!obj || typeof obj !== 'object') return;
  const rec = obj as Record<string, unknown>;
  if ('email' in rec) rec['email'] = '[redacted]';
}

/**
 * Boot Sentry once. Returns `true` if Sentry.init was called, `false`
 * if the call was skipped (no DSN configured).
 *
 * Safe to call multiple times — subsequent calls are no-ops after the
 * first successful init.
 */
export function initSentry(): boolean {
  if (__initialized) return false;
  const { dsn, gitSha } = readEnv();
  if (!dsn || dsn.trim().length === 0) {
    return false;
  }
  Sentry.init({
    dsn: dsn.trim(),
    release: gitSha && gitSha.trim().length > 0 ? gitSha.trim() : 'dev',
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    // Replay is intentionally OFF — see GDPR note in the module header.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    beforeSend: scrubEvent,
  });
  __initialized = true;
  return true;
}

/**
 * Test-only — reset the `__initialized` latch so a subsequent
 * `initSentry()` call attempts init again. Tests use this; production
 * code never does.
 */
export function resetSentryInitForTest(): void {
  __initialized = false;
}
